import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Field,
  formatDateTime,
  Sheet,
} from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import {
  errorDocsUrl,
  fetchRequestLogs,
  type RequestLogEntry,
  type RequestLogFilters,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useResource } from '../lib/resource'

/**
 * The request log (M3-06, on the M3-05 endpoint): every API call this organisation made, with the
 * status, the duration and the error code of a failure.
 *
 * The log keeps no bodies, by design — so the screen never pretends to show one. What it does show
 * is the one thing a caller can act on: the stable `code` of the problem response, as a link to the
 * page that explains that code. "422" is a number; `validation_failed` with a link is an answer.
 *
 * The detail comes from the row that is already on screen rather than from a second read: the list
 * and the detail of a request log are the same record, and asking twice would only add a way for
 * the two to disagree.
 */
export const Route = createFileRoute('/_app/logs')({
  component: Logs,
  validateSearch: (search: Record<string, unknown>): { call?: string } =>
    typeof search.call === 'string' && search.call !== '' ? { call: search.call } : {},
})

interface FilterDraft {
  readonly status: string
  readonly path: string
  readonly request_id: string
}

const NO_FILTERS: FilterDraft = { status: '', path: '', request_id: '' }

function Logs() {
  const { t, locale } = useConsoleI18n()
  const { mode } = useMode()
  const { call: open } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [draft, setDraft] = useState<FilterDraft>(NO_FILTERS)
  const [applied, setApplied] = useState<FilterDraft>(NO_FILTERS)
  const [trail, setTrail] = useState<readonly string[]>([])
  const [trailMode, setTrailMode] = useState(mode)
  const cursor = trail.at(-1)

  // Another mode is another log, so a cursor from this one means nothing there.
  if (trailMode !== mode) {
    setTrailMode(mode)
    setTrail([])
  }

  const load = useCallback(
    (signal: AbortSignal) =>
      fetchRequestLogs(
        mode,
        { ...toFilters(applied), ...(cursor === undefined ? {} : { cursor }) },
        signal,
      ),
    [mode, applied, cursor],
  )
  const { state } = useResource(load)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setTrail([])
    setApplied(draft)
  }

  const columns: readonly DataTableColumn<RequestLogEntry>[] = [
    {
      key: 'call',
      header: t('logs.column.call'),
      cell: (row) => (
        <>
          <button
            type="button"
            onClick={() => void navigate({ search: { call: row.request_id }, replace: true })}
            className="break-all text-left font-medium text-body text-brand-700 underline"
          >
            {`${row.method} ${row.path}`}
          </button>
          <span className="block font-mono text-caption text-muted">{row.request_id}</span>
        </>
      ),
    },
    {
      key: 'status',
      header: t('logs.column.status'),
      numeric: true,
      cell: (row) => (
        <span className={row.status >= 400 ? 'text-danger' : 'text-ink'}>{row.status}</span>
      ),
    },
    {
      key: 'duration',
      header: t('logs.column.duration'),
      numeric: true,
      cell: (row) => t('logs.duration', { ms: String(row.duration_ms) }),
    },
    {
      key: 'error',
      header: t('logs.column.error'),
      cell: (row) =>
        row.error_code === null ? (
          <span className="text-muted">—</span>
        ) : (
          <ErrorCodeLink code={row.error_code} />
        ),
    },
    {
      key: 'moment',
      header: t('logs.column.moment'),
      cell: (row) => (
        <span className="text-caption text-muted">{formatDateTime(row.created_at, locale)}</span>
      ),
    },
  ]

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-semibold text-display text-ink">{t('nav.logs.label')}</h1>
      <p className="text-body text-muted">{t('logs.intro')}</p>

      <form
        className="flex flex-wrap items-end gap-4 rounded-card border border-line p-4"
        onSubmit={onSubmit}
      >
        <Field label={t('logs.filter.status')}>
          {({ id }) => (
            <input
              id={id}
              type="number"
              min={100}
              max={599}
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className="min-h-touch w-28 rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field label={t('logs.filter.path')} hint={t('logs.filter.path.hint')}>
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="text"
              maxLength={200}
              value={draft.path}
              onChange={(event) => setDraft({ ...draft, path: event.target.value })}
              className="min-h-touch w-64 rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field label={t('logs.filter.request_id')}>
          {({ id }) => (
            <input
              id={id}
              type="text"
              maxLength={40}
              value={draft.request_id}
              onChange={(event) => setDraft({ ...draft, request_id: event.target.value })}
              className="min-h-touch w-72 rounded-card border border-line bg-surface px-3 font-mono text-caption text-ink"
            />
          )}
        </Field>
        <div className="flex gap-2">
          <Button type="submit">{t('filter.apply')}</Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(NO_FILTERS)
              setApplied(NO_FILTERS)
              setTrail([])
            }}
          >
            {t('filter.reset')}
          </Button>
        </div>
      </form>

      <ResourceView state={state}>
        {(page) => {
          const selected = page.data.find((row) => row.request_id === open)
          return (
            <>
              <DataTable
                caption={t('logs.table.caption')}
                columns={columns}
                rows={page.data}
                rowKey={(row) => row.request_id}
                selectedKey={open}
                empty={
                  <EmptyState title={t('logs.empty.title')} description={t('logs.empty.body')} />
                }
              />
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  disabled={trail.length === 0}
                  onClick={() => setTrail((followed) => followed.slice(0, -1))}
                >
                  {t('page.previous')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={page.next_cursor === null}
                  onClick={() =>
                    setTrail((followed) =>
                      page.next_cursor === null ? followed : [...followed, page.next_cursor],
                    )
                  }
                >
                  {t('page.next')}
                </Button>
              </div>

              {selected === undefined ? null : (
                <CallSheet
                  entry={selected}
                  onClose={() => void navigate({ search: {}, replace: true })}
                />
              )}
            </>
          )
        }}
      </ResourceView>
    </section>
  )
}

/**
 * The code, as a link to the page that explains it. The anchor is the code itself: the table on
 * /docs/errors carries one `id` per code, so `#validation_failed` lands on that row (M3-08).
 */
function ErrorCodeLink({ code }: { readonly code: string }) {
  const { t } = useConsoleI18n()
  return (
    <a
      href={errorDocsUrl(code)}
      target="_blank"
      rel="noreferrer"
      title={t('logs.error.docs')}
      className="break-all font-mono text-caption text-brand-700 underline"
    >
      {code}
    </a>
  )
}

function CallSheet({
  entry,
  onClose,
}: {
  readonly entry: RequestLogEntry
  readonly onClose: () => void
}) {
  const { t, locale } = useConsoleI18n()

  return (
    <Sheet open title={t('logs.detail.title')} closeLabel={t('button.close')} onClose={onClose}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-body">
        <dt className="text-caption text-muted">{t('logs.column.call')}</dt>
        <dd className="m-0 break-all text-ink">{`${entry.method} ${entry.path}`}</dd>

        <dt className="text-caption text-muted">{t('logs.detail.request_id')}</dt>
        <dd className="m-0 break-all font-mono text-caption text-ink">{entry.request_id}</dd>

        <dt className="text-caption text-muted">{t('logs.column.status')}</dt>
        <dd className={entry.status >= 400 ? 'm-0 text-danger' : 'm-0 text-ink'}>{entry.status}</dd>

        <dt className="text-caption text-muted">{t('logs.column.duration')}</dt>
        <dd className="m-0 text-ink">{t('logs.duration', { ms: String(entry.duration_ms) })}</dd>

        <dt className="text-caption text-muted">{t('logs.column.error')}</dt>
        <dd className="m-0">
          {entry.error_code === null ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="flex flex-wrap items-baseline gap-2">
              <ErrorCodeLink code={entry.error_code} />
              <a
                href={errorDocsUrl(entry.error_code)}
                target="_blank"
                rel="noreferrer"
                className="text-caption text-brand-700 underline"
              >
                {t('logs.error.docs')}
              </a>
            </span>
          )}
        </dd>

        <dt className="text-caption text-muted">{t('logs.column.moment')}</dt>
        <dd className="m-0 text-ink">{formatDateTime(entry.created_at, locale)}</dd>
      </dl>
      <p className="mt-4 text-caption text-muted">{t('logs.detail.no_body')}</p>
    </Sheet>
  )
}

function toFilters(draft: FilterDraft): RequestLogFilters {
  return {
    ...(draft.status.trim() === '' ? {} : { status: draft.status.trim() }),
    ...(draft.path.trim() === '' ? {} : { path: draft.path.trim() }),
    ...(draft.request_id.trim() === '' ? {} : { request_id: draft.request_id.trim() }),
  }
}
